import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Divider,
  Flex,
  Modal,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { isMobile } from 'react-device-detect';
import {
  useCloseRequestRatingModal,
  useRequestRatingModal,
} from '@worker/stores/workerStore';
import { useRatingRequestMutation } from './useRatingRequestMutation';

const askForRatingSchema = z
  .object({
    hirerEmail: z.string().email('Enter a valid email').or(z.literal('')),
    phoneNumber: z.string(),
  })
  .refine((values) => Boolean(values.hirerEmail || values.phoneNumber), {
    message: 'Enter email or cell phone',
    path: ['hirerEmail'],
  });

type AskForRatingValues = z.infer<typeof askForRatingSchema>;

export default function RequestRatingBackModal() {
  const { isOpen, jobAssignmentId, isValidatedCompany } =
    useRequestRatingModal();
  const closeModal = useCloseRequestRatingModal();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AskForRatingValues>({
    resolver: zodResolver(askForRatingSchema),
    defaultValues: { hirerEmail: '', phoneNumber: '' },
  });

  const handleClose = () => {
    reset();
    closeModal();
  };

  const mutation = useRatingRequestMutation({ onSuccess: handleClose });

  const requestValidated = () => {
    if (jobAssignmentId == null) return;
    mutation.mutate({
      jobAssignmentId,
      body: { hirerEmail: '', phoneNumber: '' },
    });
  };

  const askForRating = (values: AskForRatingValues) => {
    if (jobAssignmentId == null) return;
    mutation.mutate({ jobAssignmentId, body: values });
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      centered
      withCloseButton
      title={
        <Text fw={700} size="xl">
          Rise in Status
        </Text>
      }
    >
      {isValidatedCompany ? (
        <Stack gap="md">
          <Text fw={600}>
            Would you like to request a rating from this company?
          </Text>
          <Text size="sm" c="dimmed">
            We will share the request with your name, email, event name, brand
            name, dates worked, and a positive comment about them. No other
            information about your review will be shared. It&apos;ll be their
            choice if they&apos;d like to leave a review.
          </Text>
          <Divider />
          <Flex justify="flex-end" gap="sm" wrap="wrap">
            <Button
              variant="default"
              onClick={handleClose}
              disabled={mutation.isPending}
              fullWidth={isMobile}
            >
              No thanks
            </Button>
            <Button
              onClick={requestValidated}
              loading={mutation.isPending}
              fullWidth={isMobile}
            >
              Request rating
            </Button>
          </Flex>
        </Stack>
      ) : (
        <form onSubmit={handleSubmit(askForRating)}>
          <Stack gap="md">
            <Text fw={600}>What does this mean?</Text>
            <Text size="sm" c="dimmed">
              We&apos;ll ask them to review your performance. This will help you
              build your reputation on your profile to get more gigs!
            </Text>
            <TextInput
              label="Manager Employed at Company Email"
              placeholder="Enter email"
              type="email"
              error={errors.hirerEmail?.message}
              {...register('hirerEmail')}
            />
            <TextInput
              label="Manager Employed at Company Cell Number"
              placeholder="Enter cell number"
              error={errors.phoneNumber?.message}
              {...register('phoneNumber')}
            />
            <Divider />
            <Flex justify="flex-end" gap="sm" wrap="wrap">
              <Button
                type="button"
                variant="default"
                onClick={handleClose}
                disabled={mutation.isPending}
                fullWidth={isMobile}
              >
                No thanks
              </Button>
              <Button
                type="submit"
                loading={mutation.isPending}
                fullWidth={isMobile}
              >
                Ask for rating
              </Button>
            </Flex>
          </Stack>
        </form>
      )}
    </Modal>
  );
}
